class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));

    this.cartDrawer = document.querySelector('cart-drawer');
    this.initObserver();
    this.addCartDrawerListeners();
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }

    if (this.observer) {
      this.observer.disconnect();
    }
  }

  initObserver() {
    const cartItemsContainer = document.querySelector('#CartDrawer-CartItems');
    if (cartItemsContainer) {
      this.observer = new MutationObserver(() => {
        this.updateAllComparePrices();
      });

      // Initially, the observer should not observe changes.
      this.observer.observe(cartItemsContainer, {
        childList: true,
        subtree: true,
        attributes: true,
      });
      this.observer.disconnect(); // Disconnect initially
    }
  }

  addCartDrawerListeners() {
    if (this.cartDrawer) {
      this.cartDrawer.addEventListener('open', () => {
        this.enableObserver();
      });

      this.cartDrawer.addEventListener('close', () => {
        this.disableObserver();
      });
    }
  }

  enableObserver() {
    const cartItemsContainer = document.querySelector('#CartDrawer-CartItems');
    if (cartItemsContainer && this.observer) {
      this.observer.observe(cartItemsContainer, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    }
  }

  disableObserver() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  updateAllComparePrices() {
    const cartItems = document.querySelectorAll('.cart-item');

    cartItems.forEach((item) => {
      const quantityInput = item.querySelector('.quantity__input');
      const primaryComparePriceElement = item.querySelector(
        '.cart-item__totals .cart-item__price-wrapper span.price span.compare'
      );
      const secondaryComparePriceElement = item.querySelector(
        'div.cart-item__discounted-prices s.cart-item__old-price.price.price--end span.compare'
      );

      if (quantityInput) {
        const quantity = parseInt(quantityInput.getAttribute('value'), 10) || 1;

        // Update primary compare price if it exists
        if (primaryComparePriceElement) {
          this.updateComparePrice(primaryComparePriceElement, quantity);
        }

        // Update secondary compare price if it exists
        if (secondaryComparePriceElement) {
          this.updateComparePrice(secondaryComparePriceElement, quantity);
        }
      } else {
        console.error('Missing quantity input for item:', item);
      }
    });
  }

  updateComparePrice(comparePriceElement, quantity) {
    const comparePricePerUnit = parseFloat(
      comparePriceElement.dataset.price.replace(/[^0-9.-]+/g, '')
    );

    if (!isNaN(comparePricePerUnit)) {
      const newComparePrice = comparePricePerUnit * quantity;

      // Update the compare price element
      comparePriceElement.textContent = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(newComparePrice);
    } else {
      console.error('Invalid compare price format or missing data-price:', comparePriceElement);
    }
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name'),
      event.target.dataset.quantityVariantId
    );
  }

  onCartUpdate() {
    const isCartDrawer = this.tagName === 'CART-DRAWER-ITEMS';

    const fetchUrl = isCartDrawer
      ? `${routes.cart_url}?section_id=cart-drawer`
      : `${routes.cart_url}?section_id=main-cart-items`;

    fetch(fetchUrl)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const targetElement = isCartDrawer
          ? document.querySelector('#CartDrawer-CartItems')
          : document.querySelector('cart-items');

        if (targetElement) {
          const sourceElement = isCartDrawer
            ? html.querySelector('#CartDrawer-CartItems')
            : html.querySelector('cart-items');
          if (sourceElement) {
            targetElement.innerHTML = sourceElement.innerHTML;

            // Update compare prices after cart update
            this.updateAllComparePrices();
          }
        }
      })
      .catch((e) => console.error(e));
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);

        if (parsedState.errors) {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });

        // Update compare prices after quantity change
        this.updateAllComparePrices();
      })
      .catch(() => console.error('Error updating cart quantity'))
      .finally(() => {
        this.disableLoading(line);

        // Extend - refresh cart offers after cart update
        window.dispatchEvent(new Event('refreshAjaxSideCart'));
        // Extend - end Extend code

      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));
    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
